import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  Text,
  Flex,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { EditIcon, TriangleDownIcon } from "@chakra-ui/icons";
import {
  getAllSignatures,
  getSignatureWithUser,
  updateSignatureAdmin,
  getAllOrganizations,
  getAllUsers,
  getSignatureAudioUrlAdmin,
} from "../../services/adminService";
import { useAdminData } from "../../hooks/useAdminData";
import { createToast } from "../../utils/toastUtils";
import { usePersistentState } from "../../hooks/usePersistentState";

const SignatureManagement = () => {
  const [updating, setUpdating] = useState(false);
  const [loadingAudioId, setLoadingAudioId] = useState(null);
  const [audioUrls, setAudioUrls] = useState({});
  const [filterOrgId, setFilterOrgId] = usePersistentState(
    "admin:signatureManagement:filterOrgId",
    ""
  );
  const [filterUserId, setFilterUserId] = usePersistentState(
    "admin:signatureManagement:filterUserId",
    ""
  );
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const toastHelper = createToast(toast);

  const fetchSignaturesWithFilter = useCallback(() => {
    return getAllSignatures(filterOrgId || null, filterUserId || null);
  }, [filterOrgId, filterUserId]);

  const fetchOrganizations = useCallback(() => getAllOrganizations(), []);
  const fetchUsers = useCallback(
    () => getAllUsers(filterOrgId || null),
    [filterOrgId]
  );

  const {
    data: signaturesData,
    loading: signaturesLoading,
    error: signaturesError,
    refetch: fetchSignatures,
  } = useAdminData(fetchSignaturesWithFilter, [filterOrgId, filterUserId]);

  const {
    data: organizationsData,
    loading: orgsLoading,
    error: orgsError,
  } = useAdminData(fetchOrganizations, []);

  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useAdminData(fetchUsers, [filterOrgId]);

  const signatures = signaturesData?.signatures || [];
  const organizations = organizationsData?.organizations || [];
  const users = useMemo(() => usersData?.users || [], [usersData]);
  const loading = signaturesLoading || orgsLoading || usersLoading;
  const error = signaturesError || orgsError || usersError;

  useEffect(() => {
    if (!filterUserId) return;
    const exists = users.some((u) => String(u.id) === String(filterUserId));
    if (!exists && users.length > 0) {
      setFilterUserId("");
    }
  }, [filterUserId, setFilterUserId, users]);

  const handleOrgChange = (value) => {
    setFilterOrgId(value);
    setFilterUserId("");
    setAudioUrls({});
  };

  const handleUserChange = (value) => {
    setFilterUserId(value);
    setAudioUrls({});
  };

  const handleEditSignature = async (signature) => {
    try {
      const fullSignature = await getSignatureWithUser(signature.signature.id);
      setSelectedSignature(fullSignature);
      setEditDescription(fullSignature.signature.description || "");
      onOpen();
    } catch {
      toastHelper.error("Failed to load signature details");
    }
  };

  const handleUpdateSignature = async () => {
    if (!selectedSignature) return;
    try {
      setUpdating(true);
      await updateSignatureAdmin(selectedSignature.signature.id, {
        description: editDescription,
      });
      toastHelper.success("Signature updated");
      fetchSignatures();
      handleModalClose();
    } catch {
      toastHelper.error("Failed to update signature");
    } finally {
      setUpdating(false);
    }
  };

  const handleLoadAudio = async (signatureId) => {
    if (audioUrls[signatureId]) {
      setAudioUrls((prev) => {
        const next = { ...prev };
        delete next[signatureId];
        return next;
      });
      return;
    }
    setLoadingAudioId(signatureId);
    try {
      const response = await getSignatureAudioUrlAdmin(signatureId);
      setAudioUrls((prev) => ({
        ...prev,
        [signatureId]: response.signed_url,
      }));
    } catch (err) {
      toastHelper.error(
        "Failed to load audio",
        err.response?.data?.detail || "Could not generate signature URL"
      );
    } finally {
      setLoadingAudioId(null);
    }
  };

  const handleModalClose = () => {
    setSelectedSignature(null);
    setEditDescription("");
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        Error loading signatures: {error}
      </Alert>
    );
  }

  return (
    <VStack spacing={5} align="stretch">
      <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
        <HStack spacing={4} align="flex-end" wrap="wrap">
          <FormControl w="280px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              Organization
            </FormLabel>
            <Select
              size="sm"
              value={filterOrgId}
              onChange={(e) => handleOrgChange(e.target.value)}
              placeholder="All organizations"
              borderRadius="md"
              bg="white"
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </Select>
          </FormControl>

          <FormControl w="320px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              User
            </FormLabel>
            <Select
              size="sm"
              value={filterUserId}
              onChange={(e) => handleUserChange(e.target.value)}
              placeholder="All users"
              borderRadius="md"
              bg="white"
            >
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.email}</option>
              ))}
            </Select>
          </FormControl>
        </HStack>

        <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={3} py={1} fontSize="xs">
          {signatures.length} signatures
        </Badge>
      </Flex>

      {signatures.length === 0 ? (
        <Box
          py={16} textAlign="center" bg="gray.50" borderRadius="lg"
          borderWidth="1px" borderColor="gray.200" borderStyle="dashed"
        >
          <Text color="gray.400" fontSize="sm">No signatures found</Text>
        </Box>
      ) : (
        <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
          <Table variant="unstyled" size="sm">
            <Thead>
              <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                {["User", "Source", "Version", "Description", "Created", "Audio", ""].map((h) => (
                  <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">{h}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {signatures.map((item, idx) => {
                const sig = item.signature;
                return (
                  <Tr
                    key={sig.id}
                    borderBottomWidth={idx < signatures.length - 1 ? "1px" : "0"}
                    borderBottomColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                    transition="background 0.1s"
                  >
                    <Td py={3} px={4}>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="semibold">{item.user.email}</Text>
                        <Text fontSize="xs" color="gray.500">{item.organization.name}</Text>
                      </VStack>
                    </Td>
                    <Td py={3} px={4}>
                      <VStack align="start" spacing={1}>
                        <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2} fontSize="xs">
                          {sig.device_type || "UNKNOWN"}
                        </Badge>
                        <Text fontSize="xs" color="gray.500" fontFamily="mono">
                          {sig.source_key || "legacy"}
                        </Text>
                      </VStack>
                    </Td>
                    <Td py={3} px={4}>
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.600">v{sig.version ?? "-"}</Text>
                        <Badge
                          colorScheme={sig.is_active ? "green" : "gray"}
                          variant="subtle"
                          borderRadius="full"
                          px={2}
                          fontSize="xs"
                        >
                          {sig.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </HStack>
                    </Td>
                    <Td py={3} px={4} maxW="240px">
                      <Text fontSize="sm" color="gray.600" isTruncated>
                        {sig.description || "-"}
                      </Text>
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="xs" color="gray.400">{formatDate(sig.created_at)}</Text>
                    </Td>
                    <Td py={3} px={4} minW="280px">
                      <VStack align="stretch" spacing={2}>
                        <Button
                          leftIcon={<TriangleDownIcon boxSize={2.5} />}
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          w="fit-content"
                          isLoading={loadingAudioId === sig.id}
                          onClick={() => handleLoadAudio(sig.id)}
                        >
                          {audioUrls[sig.id] ? "Hide Player" : "Play Signature"}
                        </Button>
                        {audioUrls[sig.id] && (
                          <Box
                            as="audio"
                            controls
                            src={audioUrls[sig.id]}
                            w="260px"
                          />
                        )}
                      </VStack>
                    </Td>
                    <Td py={3} px={4} textAlign="right">
                      <IconButton
                        icon={<EditIcon />} size="xs" colorScheme="blue" variant="ghost"
                        borderRadius="md" aria-label="Edit signature"
                        onClick={() => handleEditSignature(item)}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader
            pb={3} pt={6} px={6} fontSize="lg" fontWeight="semibold"
            borderBottomWidth="1px" borderBottomColor="gray.100"
          >
            Edit Signature
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />
          <ModalBody px={6} py={5}>
            {selectedSignature && (
              <VStack spacing={4} align="stretch">
                <Box bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200" px={4} py={3}>
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={2}>
                    Signature
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">{selectedSignature.user.email}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {selectedSignature.signature.device_type || "UNKNOWN"} / {selectedSignature.signature.source_key || "legacy"} / v{selectedSignature.signature.version ?? "-"}
                  </Text>
                </Box>

                <FormControl>
                  <FormLabel fontSize="sm">Description</FormLabel>
                  <Input
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter signature description"
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter px={6} py={4} borderTopWidth="1px" borderTopColor="gray.100" gap={2}>
            <Button variant="ghost" size="sm" onClick={handleModalClose}>Cancel</Button>
            <Button
              colorScheme="blue" size="sm" px={6}
              onClick={handleUpdateSignature}
              isLoading={updating}
              loadingText="Updating..."
              isDisabled={
                !selectedSignature ||
                editDescription === (selectedSignature?.signature.description || "")
              }
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default SignatureManagement;
